import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, PhoneOff, PhoneIncoming, PhoneOutgoing, Mic, MicOff, Volume2, VolumeX, ArrowRightLeft, Hash, Minimize2, Maximize2, Power, User, Briefcase, ChevronRight, ChevronLeft, BarChart3, PhoneForwarded, Loader2, Search, Snowflake, Flame, X, Check, Bot } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useSoftphoneContext } from '@/contexts/SoftphoneContext';
import { useCallerLookup } from '@/hooks/useCallerLookup';
import { useSwitchboardCalls } from '@/hooks/useSwitchboard';
import { useIvrLabel } from '@/hooks/useIvrLabel';
import { useDequeueCall } from '@/hooks/useDequeueCall';
import { useCompanyMembers } from '@/hooks/useCompanyMembers';
import { AgentStatusSelector } from '@/components/softphone/AgentStatusSelector';
import { CallSummariesTab } from '@/components/switchboard/CallSummariesTab';

function CallerInfo({ phoneNumber }: { phoneNumber: string }) {
  const navigate = useNavigate();
  const { customer, projects, isLoading } = useCallerLookup(phoneNumber);

  if (isLoading) {
    return (
      <div className="px-3 py-2 border-t border-border">
        <p className="text-[11px] text-muted-foreground animate-pulse">Søger kunde...</p>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="px-3 py-2 border-t border-border">
        <p className="text-[11px] text-muted-foreground">Ukendt nummer – ingen kunde fundet</p>
      </div>
    );
  }

  return (
    <div className="px-3 py-2 border-t border-border space-y-2">
      <button
        className="flex items-center gap-2 w-full text-left hover:bg-accent/50 rounded p-1.5 -m-1 transition-colors"
        onClick={() => navigate(`/office/customers/${customer.id}`)}
      >
        <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
          <User className="w-3.5 h-3.5 text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-foreground truncate">{customer.name}</p>
          {customer.contact_person && (
            <p className="text-[10px] text-muted-foreground truncate">{customer.contact_person}</p>
          )}
        </div>
        <ChevronRight className="w-3 h-3 text-muted-foreground shrink-0" />
      </button>

      {projects.length > 0 && (
        <div className="space-y-1">
          <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
            Aktive sager ({projects.length})
          </p>
          {projects.slice(0, 3).map(p => (
            <button
              key={p.id}
              className="flex items-center gap-1.5 w-full text-left hover:bg-accent/50 rounded px-1.5 py-1 transition-colors"
              onClick={() => navigate(`/office/cases/${p.id}`)}
            >
              <Briefcase className="w-3 h-3 text-muted-foreground shrink-0" />
              <span className="text-[11px] text-foreground truncate flex-1">{p.title}</span>
              <Badge variant="outline" className="text-[8px] h-4 shrink-0">{p.status}</Badge>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function QueuedCallerItem({ call, onBeforeDequeue }: { call: any; onBeforeDequeue?: () => void }) {
  const { customer } = useCallerLookup(call.caller_number || '');
  const { dequeue, isDequeuing } = useDequeueCall(onBeforeDequeue);

  const waitTime = call.created_at
    ? Math.floor((Date.now() - new Date(call.created_at).getTime()) / 1000)
    : 0;
  const waitMin = Math.floor(waitTime / 60);
  const waitSec = waitTime % 60;

  return (
    <div className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-muted/50 hover:bg-muted transition-colors">
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-medium text-foreground truncate">
          {customer?.name || call.caller_number || 'Ukendt'}
        </p>
        <p className="text-[9px] text-muted-foreground">
          {customer ? call.caller_number : ''} · {waitMin > 0 ? `${waitMin}m ` : ''}{waitSec}s
        </p>
      </div>
      <Button
        size="sm"
        className="h-6 px-2 text-[10px] gap-1 bg-emerald-500 hover:bg-emerald-600 text-white"
        disabled={isDequeuing}
        onClick={() => call.twilio_call_sid && dequeue(call.twilio_call_sid)}
      >
        {isDequeuing ? <Loader2 className="w-3 h-3 animate-spin" /> : <PhoneForwarded className="w-3 h-3" />}
        Tag
      </Button>
    </div>
  );
}

function QueueStats({ onBeforeDequeue }: { onBeforeDequeue?: () => void }) {
  const { data: activeCalls } = useSwitchboardCalls('active');
  const { data: historyCalls } = useSwitchboardCalls('history');

  const queuedCalls = activeCalls?.filter(c => c.status === 'queued' && c.direction === 'inbound') ?? [];
  const inProgress = activeCalls?.filter(c => c.status === 'in-progress').length ?? 0;

  // Today's stats from history
  const today = new Date().toISOString().slice(0, 10);
  const todayCalls = historyCalls?.filter(c => c.created_at?.startsWith(today)) ?? [];
  const missed = todayCalls.filter(c => c.status === 'no-answer' || c.status === 'missed').length;
  const answered = todayCalls.filter(c => c.answered_at).length;

  return (
    <div className="px-3 py-2 border-t border-border space-y-2">
      {/* Queued callers list */}
      {queuedCalls.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[10px] font-medium text-amber-500 uppercase tracking-wider flex items-center gap-1">
            <PhoneIncoming className="w-3 h-3" />
            I kø ({queuedCalls.length})
          </p>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {queuedCalls.map(call => (
              <QueuedCallerItem key={call.id} call={call} onBeforeDequeue={onBeforeDequeue} />
            ))}
          </div>
        </div>
      )}

      <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1">
        <BarChart3 className="w-3 h-3" /> Oversigt
      </p>
      <div className="grid grid-cols-2 gap-2">
        <StatBox label="I kø" value={queuedCalls.length} color={queuedCalls.length > 0 ? 'text-amber-500' : 'text-muted-foreground'} />
        <StatBox label="Aktive" value={inProgress} color={inProgress > 0 ? 'text-blue-500' : 'text-muted-foreground'} />
        <StatBox label="Besvaret i dag" value={answered} color="text-emerald-500" />
        <StatBox label="Ubesvaret" value={missed} color={missed > 0 ? 'text-red-500' : 'text-muted-foreground'} />
      </div>
    </div>
  );
}

function StatBox({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="bg-muted/50 rounded-md px-2 py-1.5 text-center">
      <p className={cn('text-base font-bold', color)}>{value}</p>
      <p className="text-[9px] text-muted-foreground">{label}</p>
    </div>
  );
}

function TransferPanel({ phone, onClose }: { phone: ReturnType<typeof useSoftphoneContext>; onClose: () => void }) {
  const [search, setSearch] = useState('');
  const [manualNumber, setManualNumber] = useState('');
  const { data: members = [] } = useCompanyMembers();

  const filtered = useMemo(() => {
    if (!search) return members;
    const q = search.toLowerCase();
    return members.filter(m => m.full_name.toLowerCase().includes(q) || m.phone.includes(q));
  }, [members, search]);

  const handleTransfer = (number: string, type: 'cold' | 'warm') => {
    if (type === 'cold') phone.coldTransfer(number);
    else phone.warmTransfer(number);
    onClose();
  };

  return (
    <div className="space-y-2 border-t border-border pt-2">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Viderestil</p>
        <Button variant="ghost" size="icon" className="h-5 w-5" onClick={onClose}>
          <X className="w-3 h-3" />
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
        <Input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Søg medarbejder..."
          className="h-7 text-[11px] pl-7"
        />
      </div>

      <div className="max-h-32 overflow-y-auto space-y-0.5">
        {filtered.map(m => (
          <div key={m.id} className="flex items-center gap-1.5 px-1.5 py-1 rounded hover:bg-muted/50 transition-colors">
            <div className="h-5 w-5 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <User className="w-2.5 h-2.5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-medium text-foreground truncate">{m.full_name}</p>
              <p className="text-[9px] text-muted-foreground font-mono">{m.phone}</p>
            </div>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0"
              title="Kold viderestilling"
              onClick={() => handleTransfer(m.phone, 'cold')}
            >
              <Snowflake className="w-3 h-3 text-blue-500" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0"
              title="Varm viderestilling"
              onClick={() => handleTransfer(m.phone, 'warm')}
            >
              <Flame className="w-3 h-3 text-orange-500" />
            </Button>
          </div>
        ))}
        {filtered.length === 0 && (
          <p className="text-[10px] text-muted-foreground text-center py-2">Ingen medarbejdere fundet</p>
        )}
      </div>

      {/* Manual number fallback */}
      <div className="flex gap-1 pt-1 border-t border-border">
        <Input
          value={manualNumber}
          onChange={e => setManualNumber(e.target.value)}
          placeholder="Andet nummer..."
          className="h-7 text-[11px] font-mono"
        />
        <Button
          size="sm"
          variant="outline"
          className="h-7 px-1.5 shrink-0"
          disabled={!manualNumber}
          title="Kold"
          onClick={() => handleTransfer(manualNumber, 'cold')}
        >
          <Snowflake className="w-3 h-3 text-blue-500" />
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-7 px-1.5 shrink-0"
          disabled={!manualNumber}
          title="Varm"
          onClick={() => handleTransfer(manualNumber, 'warm')}
        >
          <Flame className="w-3 h-3 text-orange-500" />
        </Button>
      </div>
    </div>
  );
}

/** Embeddable softphone content — no fixed positioning wrapper */
export function EmbeddedSoftphoneContent({ onBack }: { onBack?: () => void }) {
  const phone = useSoftphoneContext();
  const ivrLabel = useIvrLabel();
  const [dialNumber, setDialNumber] = useState('');
  const [showDialpad, setShowDialpad] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [showTransfer, setShowTransfer] = useState(false);
  const [bottomTab, setBottomTab] = useState<'queue' | 'summaries'>('queue');

  const { data: activeCalls } = useSwitchboardCalls('active');
  const activePhoneNumber = phone.callerNumber || null;
  const matchingCall = activeCalls?.find(c =>
    c.caller_number === phone.callerNumber && (c.status === 'ringing' || c.status === 'queued' || c.status === 'in-progress')
  );

  const fmtDuration = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const statusColor: Record<string, string> = {
    offline: 'bg-muted text-muted-foreground',
    ready: 'bg-emerald-500/10 text-emerald-600 border-emerald-200',
    connecting: 'bg-amber-500/10 text-amber-600 border-amber-200',
    'on-call': 'bg-blue-500/10 text-blue-600 border-blue-200',
    ringing: 'bg-red-500/10 text-red-600 border-red-200 animate-pulse',
    consulting: 'bg-purple-500/10 text-purple-600 border-purple-200',
  };

  const statusLabel: Record<string, string> = {
    offline: 'Offline',
    ready: 'Klar',
    connecting: 'Forbinder...',
    'on-call': 'I opkald',
    ringing: 'Indgående opkald',
    consulting: 'Konsultation',
  };

  // Loading state while device initializes
  if (phone.status === 'offline' && !phone.isInitialized) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-12 px-4">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        <p className="text-xs text-muted-foreground">Opretter forbindelse til telefonsystem...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="px-3 py-2 bg-muted/50 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          {onBack && (
            <button onClick={onBack} className="w-6 h-6 rounded-full hover:bg-secondary flex items-center justify-center">
              <ChevronLeft className="w-4 h-4 text-muted-foreground" />
            </button>
          )}
          <Phone className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="text-xs font-semibold text-foreground">Omstilling</span>
          <AgentStatusSelector compact />
        </div>
        <div className="flex items-center gap-1">
          <Badge variant="outline" className={cn('text-[9px] h-5', statusColor[phone.status])}>
            {statusLabel[phone.status]}
          </Badge>
        </div>
      </div>

      {/* Incoming call alert */}
      {phone.status === 'ringing' && phone.incomingCall && (
        <div className="p-4 bg-red-50 dark:bg-red-950/20 space-y-3">
          <div className="flex items-center gap-2 animate-pulse">
            <PhoneIncoming className="w-5 h-5 text-red-500" />
            <div>
              <p className="text-sm font-bold text-foreground">Indgående opkald</p>
              <p className="text-xs text-muted-foreground font-mono">{phone.callerNumber}</p>
            </div>
            {matchingCall?.ivr_selection && (
              <Badge variant="outline" className="text-[10px] ml-auto">
                {ivrLabel(matchingCall.ivr_selection)}
              </Badge>
            )}
          </div>
          <div className="flex gap-2">
            <Button size="sm" className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white gap-1" onClick={phone.acceptCall}>
              <Phone className="w-3.5 h-3.5" /> Besvar
            </Button>
            <Button size="sm" variant="destructive" className="flex-1 gap-1" onClick={phone.rejectCall}>
              <PhoneOff className="w-3.5 h-3.5" /> Afvis
            </Button>
          </div>
        </div>
      )}

      {/* Caller lookup */}
      {(phone.status === 'ringing' || phone.status === 'on-call') && activePhoneNumber && (
        <CallerInfo phoneNumber={activePhoneNumber} />
      )}

      {/* Active call */}
      {phone.status === 'on-call' && (
        <div className="p-3 space-y-3">
          <div className="text-center space-y-1">
            <div className="flex items-center justify-center gap-2">
              <p className="text-xs text-muted-foreground">
                {phone.callDirection === 'inbound' ? 'Indgående' : 'Udgående'}
              </p>
              {matchingCall?.ivr_selection && (
                <Badge variant="outline" className="text-[9px]">
                  {ivrLabel(matchingCall.ivr_selection)}
                </Badge>
              )}
            </div>
            <p className="text-sm font-bold text-foreground font-mono">{phone.callerNumber}</p>
            <p className="text-lg font-mono text-blue-500 font-bold">{fmtDuration(phone.callDuration)}</p>
          </div>

          <div className="flex justify-center gap-2">
            <Button size="icon" variant={phone.isMuted ? 'destructive' : 'outline'} className="h-9 w-9 rounded-full" onClick={phone.toggleMute} title={phone.isMuted ? 'Slå mikrofon til' : 'Slå mikrofon fra'}>
              {phone.isMuted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </Button>
            <Button size="icon" variant={phone.isSpeakerMuted ? 'destructive' : 'outline'} className="h-9 w-9 rounded-full" onClick={phone.toggleSpeaker} title={phone.isSpeakerMuted ? 'Slå højtaler til' : 'Slå højtaler fra'}>
              {phone.isSpeakerMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </Button>
            <Button size="icon" variant="outline" className="h-9 w-9 rounded-full" onClick={() => setShowDialpad(!showDialpad)} title="Tastatur">
              <Hash className="w-4 h-4" />
            </Button>
            <Button size="icon" variant="outline" className="h-9 w-9 rounded-full" onClick={() => setShowTransfer(!showTransfer)} title="Viderestil">
              <ArrowRightLeft className="w-4 h-4" />
            </Button>
            <Button size="icon" variant="destructive" className="h-9 w-9 rounded-full" onClick={phone.hangUp} title="Læg på">
              <PhoneOff className="w-4 h-4" />
            </Button>
          </div>

          {showDialpad && (
            <div className="grid grid-cols-3 gap-1">
              {['1','2','3','4','5','6','7','8','9','*','0','#'].map(d => (
                <Button key={d} variant="outline" size="sm" className="h-8 text-sm font-mono" onClick={() => phone.sendDigit(d)}>{d}</Button>
              ))}
            </div>
          )}
          {showTransfer && <TransferPanel phone={phone} onClose={() => setShowTransfer(false)} />}
        </div>
      )}

      {/* Idle / Ready */}
      {(phone.status === 'ready' || phone.status === 'offline') && (
        <div className="p-3 space-y-2">
          {phone.status === 'offline' && (
            <div className="text-center space-y-2">
              <p className="text-xs text-muted-foreground animate-pulse">Forbinder telefon...</p>
              <Button size="sm" variant="outline" className="w-full gap-1.5 text-xs" onClick={phone.initialize}>
                <Power className="w-3.5 h-3.5" /> Genstart telefon
              </Button>
            </div>
          )}
          {phone.status === 'ready' && (
            <div className="flex gap-1">
              <Input value={dialNumber} onChange={e => setDialNumber(e.target.value)} placeholder="Indtast nummer..." className="h-8 text-sm font-mono"
                onKeyDown={e => { if (e.key === 'Enter' && dialNumber) phone.makeCall(dialNumber); }} />
              <Button size="icon" className="h-8 w-8 bg-emerald-500 hover:bg-emerald-600 text-white shrink-0" disabled={!dialNumber}
                onClick={() => { phone.makeCall(dialNumber); setDialNumber(''); }}>
                <PhoneOutgoing className="w-3.5 h-3.5" />
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Connecting */}
      {phone.status === 'connecting' && (
        <div className="p-4 text-center">
          <p className="text-sm text-muted-foreground animate-pulse">Forbinder...</p>
          <p className="text-xs font-mono text-foreground mt-1">{phone.callerNumber}</p>
        </div>
      )}

      {/* Consulting */}
      {phone.status === 'consulting' && (
        <div className="p-3 space-y-3">
          <div className="text-center space-y-1">
            <Badge className="bg-purple-500/10 text-purple-600 border-purple-200">
              <Flame className="w-3 h-3 mr-1" /> Konsultation
            </Badge>
            <p className="text-sm font-bold text-foreground mt-2">
              {phone.activeCall ? `Taler med ${phone.consultingNumber}` : `Forbinder til ${phone.consultingNumber}...`}
            </p>
            <p className="text-xs text-muted-foreground">
              {phone.activeCall ? 'Kunden venter. Tryk "Overfør" for at forbinde.' : 'Vent på at konsultationen forbindes, før du overfører.'}
            </p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white gap-1" onClick={phone.completeWarmTransfer} disabled={!phone.activeCall}>
              {!phone.activeCall ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Overfør
            </Button>
            <Button size="sm" variant="destructive" className="flex-1 gap-1" onClick={phone.cancelWarmTransfer}>
              <X className="w-3.5 h-3.5" /> Annullér
            </Button>
          </div>
        </div>
      )}

      {/* Bottom tabs: Queue / Summaries */}
      <div className="border-t border-border">
        <div className="flex border-b border-border">
          <button
            className={cn(
              'flex-1 text-[10px] font-medium py-1.5 text-center transition-colors flex items-center justify-center gap-1',
              bottomTab === 'queue' ? 'text-foreground border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'
            )}
            onClick={() => setBottomTab('queue')}
          >
            <BarChart3 className="w-3 h-3" /> Kø & Oversigt
          </button>
          <button
            className={cn(
              'flex-1 text-[10px] font-medium py-1.5 text-center transition-colors flex items-center justify-center gap-1',
              bottomTab === 'summaries' ? 'text-foreground border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'
            )}
            onClick={() => setBottomTab('summaries')}
          >
            <Bot className="w-3 h-3" /> Resuméer
          </button>
        </div>
        {bottomTab === 'queue' ? (
          <QueueStats onBeforeDequeue={() => phone.setPendingDequeue(true)} />
        ) : (
          <CallSummariesTab />
        )}
      </div>
    </div>
  );
}

export function SoftphoneWidget() {
  const phone = useSoftphoneContext();
  const ivrLabel = useIvrLabel();
  const [dialNumber, setDialNumber] = useState('');
  const [showDialpad, setShowDialpad] = useState(false);
  const [minimized, setMinimized] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);

  // Listen for toggle event from TabBar shortcut
  useEffect(() => {
    const handler = () => setMinimized(prev => !prev);
    window.addEventListener('toggle-softphone', handler);
    return () => window.removeEventListener('toggle-softphone', handler);
  }, []);

  // Auto-expand when queued calls appear
  const { data: activeCalls } = useSwitchboardCalls('active');
  const queuedCount = activeCalls?.filter(c => c.status === 'queued' && c.direction === 'inbound').length ?? 0;

  // Track previous queued count to detect new arrivals
  const prevQueuedRef = useRef(0);
  useEffect(() => {
    if (queuedCount > 0 && queuedCount > prevQueuedRef.current) {
      setMinimized(false);
      setExpanded(true);
    }
    prevQueuedRef.current = queuedCount;
  }, [queuedCount]);

  // Auto-expand when consulting (warm transfer)
  useEffect(() => {
    if (phone.status === 'consulting') {
      setMinimized(false);
    }
  }, [phone.status]);

  const activePhoneNumber = phone.callerNumber || null;

  // Find matching switchboard call for IVR selection
  const matchingCall = activeCalls?.find(c =>
    c.caller_number === phone.callerNumber && (c.status === 'ringing' || c.status === 'queued' || c.status === 'in-progress')
  );

  const fmtDuration = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const statusColor: Record<string, string> = {
    offline: 'bg-muted text-muted-foreground',
    ready: 'bg-emerald-500/10 text-emerald-600 border-emerald-200',
    connecting: 'bg-amber-500/10 text-amber-600 border-amber-200',
    'on-call': 'bg-blue-500/10 text-blue-600 border-blue-200',
    ringing: 'bg-red-500/10 text-red-600 border-red-200 animate-pulse',
    consulting: 'bg-purple-500/10 text-purple-600 border-purple-200',
  };

  const statusLabel: Record<string, string> = {
    offline: 'Offline',
    ready: 'Klar',
    connecting: 'Forbinder...',
    'on-call': 'I opkald',
    ringing: 'Indgående opkald',
    consulting: 'Konsultation',
  };

  // Minimized view
  if (minimized && phone.status !== 'ringing') {
    return (
      <div className="fixed bottom-4 right-4 z-50">
         <Button
          size="icon"
          className={cn(
            'h-12 w-12 rounded-full shadow-lg',
            phone.status === 'on-call' ? 'bg-blue-500 hover:bg-blue-600 text-white' :
            phone.status === 'consulting' ? 'bg-purple-500 hover:bg-purple-600 text-white' :
            phone.status === 'ready' ? 'bg-emerald-500 hover:bg-emerald-600 text-white' :
            'bg-muted hover:bg-muted/80'
          )}
          onClick={() => setMinimized(false)}
        >
          <Phone className="w-5 h-5" />
        </Button>
        {(phone.status === 'on-call' || phone.status === 'consulting') && (
          <Badge className={cn("absolute -top-1 -left-1 text-[9px] text-white", phone.status === 'consulting' ? 'bg-purple-500' : 'bg-blue-500')}>
            {phone.status === 'consulting' ? 'Konsultation' : fmtDuration(phone.callDuration)}
          </Badge>
        )}
      </div>
    );
  }

  return (
    <div className={cn(
      "fixed bottom-4 right-4 z-50 bg-card border border-border rounded-xl shadow-2xl overflow-hidden transition-all duration-200",
      expanded ? 'w-80' : 'w-64'
    )}>
      {/* Header */}
      <div className="px-3 py-2 bg-muted/50 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Phone className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="text-xs font-semibold text-foreground">Omstilling</span>
          <AgentStatusSelector compact />
        </div>
        <div className="flex items-center gap-1">
          <Badge variant="outline" className={cn('text-[9px] h-5', statusColor[phone.status])}>
            {statusLabel[phone.status]}
          </Badge>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => {
              if (expanded) setExpanded(false);
              else if (!expanded) { setExpanded(true); }
            }}
            onDoubleClick={() => { setMinimized(true); setExpanded(false); }}
            title="Klik: udvid/kompakt · Dobbeltklik: minimer"
          >
            {expanded ? <Minimize2 className="w-3 h-3" /> : <Maximize2 className="w-3 h-3" />}
          </Button>
        </div>
      </div>

      {/* Incoming call alert */}
      {phone.status === 'ringing' && phone.incomingCall && (
        <div className="p-4 bg-red-50 dark:bg-red-950/20 space-y-3">
          <div className="flex items-center gap-2 animate-pulse">
            <PhoneIncoming className="w-5 h-5 text-red-500" />
            <div>
              <p className="text-sm font-bold text-foreground">Indgående opkald</p>
              <p className="text-xs text-muted-foreground font-mono">{phone.callerNumber}</p>
            </div>
            {matchingCall?.ivr_selection && (
              <Badge variant="outline" className="text-[10px] ml-auto">
                {ivrLabel(matchingCall.ivr_selection)}
              </Badge>
            )}
          </div>
          <div className="flex gap-2">
            <Button size="sm" className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white gap-1" onClick={phone.acceptCall}>
              <Phone className="w-3.5 h-3.5" /> Besvar
            </Button>
            <Button size="sm" variant="destructive" className="flex-1 gap-1" onClick={phone.rejectCall}>
              <PhoneOff className="w-3.5 h-3.5" /> Afvis
            </Button>
          </div>
        </div>
      )}

      {/* Caller lookup when ringing or on-call */}
      {(phone.status === 'ringing' || phone.status === 'on-call') && activePhoneNumber && (
        <CallerInfo phoneNumber={activePhoneNumber} />
      )}

      {/* Active call */}
      {phone.status === 'on-call' && (
        <div className="p-3 space-y-3">
          <div className="text-center space-y-1">
            <div className="flex items-center justify-center gap-2">
              <p className="text-xs text-muted-foreground">
                {phone.callDirection === 'inbound' ? 'Indgående' : 'Udgående'}
              </p>
              {matchingCall?.ivr_selection && (
                <Badge variant="outline" className="text-[9px]">
                  {ivrLabel(matchingCall.ivr_selection)}
                </Badge>
              )}
            </div>
            <p className="text-sm font-bold text-foreground font-mono">{phone.callerNumber}</p>
            <p className="text-lg font-mono text-blue-500 font-bold">{fmtDuration(phone.callDuration)}</p>
          </div>

          <div className="flex justify-center gap-2">
            <Button
              size="icon"
              variant={phone.isMuted ? 'destructive' : 'outline'}
              className="h-9 w-9 rounded-full"
              onClick={phone.toggleMute}
              title={phone.isMuted ? 'Unmute' : 'Mute'}
            >
              {phone.isMuted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </Button>

            <Button
              size="icon"
              variant="outline"
              className="h-9 w-9 rounded-full"
              onClick={() => setShowDialpad(!showDialpad)}
              title="Nummertaster"
            >
              <Hash className="w-4 h-4" />
            </Button>

            <Button
              size="icon"
              variant="outline"
              className="h-9 w-9 rounded-full"
              onClick={() => setShowTransfer(!showTransfer)}
              title="Viderestil"
            >
              <ArrowRightLeft className="w-4 h-4" />
            </Button>

            <Button
              size="icon"
              variant="destructive"
              className="h-9 w-9 rounded-full"
              onClick={phone.hangUp}
              title="Læg på"
            >
              <PhoneOff className="w-4 h-4" />
            </Button>
          </div>

          {/* DTMF dialpad */}
          {showDialpad && (
            <div className="grid grid-cols-3 gap-1">
              {['1','2','3','4','5','6','7','8','9','*','0','#'].map(d => (
                <Button
                  key={d}
                  variant="outline"
                  size="sm"
                  className="h-8 text-sm font-mono"
                  onClick={() => phone.sendDigit(d)}
                >
                  {d}
                </Button>
              ))}
            </div>
          )}

          {/* Transfer UI */}
          {showTransfer && <TransferPanel phone={phone} onClose={() => setShowTransfer(false)} />}
        </div>
      )}

      {/* Idle / Ready - dial out */}
      {(phone.status === 'ready' || phone.status === 'offline') && (
        <div className="p-3 space-y-2">
      {phone.status === 'offline' && (
            <div className="text-center space-y-2">
              <p className="text-xs text-muted-foreground animate-pulse">Forbinder telefon...</p>
              <Button
                size="sm"
                variant="outline"
                className="w-full gap-1.5 text-xs"
                onClick={phone.initialize}
              >
                <Power className="w-3.5 h-3.5" /> Genstart telefon
              </Button>
            </div>
          )}

          {phone.status === 'ready' && (
            <div className="flex gap-1">
              <Input
                value={dialNumber}
                onChange={e => setDialNumber(e.target.value)}
                placeholder="Indtast nummer..."
                className="h-8 text-sm font-mono"
                onKeyDown={e => {
                  if (e.key === 'Enter' && dialNumber) phone.makeCall(dialNumber);
                }}
              />
              <Button
                size="icon"
                className="h-8 w-8 bg-emerald-500 hover:bg-emerald-600 text-white shrink-0"
                disabled={!dialNumber}
                onClick={() => {
                  phone.makeCall(dialNumber);
                  setDialNumber('');
                }}
              >
                <PhoneOutgoing className="w-3.5 h-3.5" />
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Connecting state */}
      {phone.status === 'connecting' && (
        <div className="p-4 text-center">
          <p className="text-sm text-muted-foreground animate-pulse">Forbinder...</p>
          <p className="text-xs font-mono text-foreground mt-1">{phone.callerNumber}</p>
        </div>
      )}

      {/* Consulting state (warm transfer in progress) */}
      {phone.status === 'consulting' && (
        <div className="p-3 space-y-3">
          <div className="text-center space-y-1">
            <Badge className="bg-purple-500/10 text-purple-600 border-purple-200">
              <Flame className="w-3 h-3 mr-1" /> Konsultation
            </Badge>
            <p className="text-sm font-bold text-foreground mt-2">
              {phone.activeCall ? `Taler med ${phone.consultingNumber}` : `Forbinder til ${phone.consultingNumber}...`}
            </p>
            <p className="text-xs text-muted-foreground">
              {phone.activeCall
                ? 'Kunden venter. Tryk "Overfør" for at forbinde.'
                : 'Vent på at konsultationen forbindes, før du overfører.'}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white gap-1"
              onClick={phone.completeWarmTransfer}
              disabled={!phone.activeCall}
            >
              {!phone.activeCall ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Overfør
            </Button>
            <Button
              size="sm"
              variant="destructive"
              className="flex-1 gap-1"
              onClick={phone.cancelWarmTransfer}
            >
              <X className="w-3.5 h-3.5" /> Annullér
            </Button>
          </div>
        </div>
      )}

      {/* Expanded: Queue stats dashboard */}
      {expanded && phone.status !== 'ringing' && (
        <QueueStats onBeforeDequeue={() => phone.setPendingDequeue(true)} />
      )}
    </div>
  );
}
